import React, { Dispatch, SetStateAction } from "react";
import {
  ICreateEmbeddedExhibitorRes,
  TwoFactorMethodType,
} from "@evennt-tech/api-v2-external";
import {
  IBuildType,
  IExhibitorCompany,
  IShowLocalMap,
  ShowUnitSystem,
} from "@evennt-tech/api-v2-core";
import { AnonymousOldShow } from "ts/api/show";

export type CredentialsFormState = {
  companyName: string;
  standNumber: string;
  email: string;
};
export type StandDetailsFormState = {
  firstName: string;
  lastName: string;
  companyAddress?: string;
  companyPostcode?: string;
  contactNumber?: string;
  buildType: string;
  standWidth?: number;
  standLength?: number;
  irregularArea?: number;
  wallLength?: number;
  isIrregularSelected?: boolean;
};
export type ExtendedShow = AnonymousOldShow & {
  organiserLogo?: string;
  organiserCompany?: string;
  buildTypes: IBuildType[];
  unitSystem: ShowUnitSystem;
  localMap: IShowLocalMap;
};

/**
 * Mirrors `ExhibitorEmbeddedValidateReason` in `@evennt-tech/api-v2-function`.
 * Re-declared rather than imported because that package is the cloud-function
 * half of the contract and the console does not depend on it — the values must
 * be kept in step with it by hand.
 */
export enum ValidateExhibitorReason {
  NewStand = "new_stand",
  OwnStand = "own_stand",
  StandHeldByCompany = "stand_held_by_company",
  StandHeldUnderOtherCompany = "stand_held_under_other_company",
  StandHeldByOthers = "stand_held_by_others",
  CompanyHasOwnStands = "company_has_own_stands",
  CompanyHasOtherStands = "company_has_other_stands",
  EmailInUseByAnotherRole = "email_in_use_by_another_role",
}

export type ValidateExhibitorResponse = {
  status: "success" | "warning" | "error";
  message?: string;
  /** Absent when the API predates the reason codes. */
  reason?: ValidateExhibitorReason;
  exhibitorCompany?: IExhibitorCompany;
};

/** A completed Google sign-in, waiting to be spent by the sign-up. */
export type GoogleIdentity = {
  identityToken: string;
  email: string;
};

/** A second factor the existing account asked for before its session starts. */
export type GoogleTwoFactorState = {
  pendingToken: string;
  method: TwoFactorMethodType;
  methods: TwoFactorMethodType[];
};
export type SignUpResult = ICreateEmbeddedExhibitorRes | null;
export type MultiStepFormState = {
  credentials: CredentialsFormState;
  standDetails: StandDetailsFormState;
  activeStep: number;
  show: ExtendedShow;
  isShowLoading: boolean;
  validationResult: ValidateExhibitorResponse | null;
  /**
   * Why the last validate request failed when it may succeed on retry (rate
   * limit, invalid input, outage). Unlike an `error` validation result it
   * does not lock the credentials — the same input can be submitted again.
   */
  submitError: string | null;
  signUpResult: SignUpResult;
  googleIdentity: GoogleIdentity | null;
  /** Set when the existing account needs a second factor; the page shows the challenge. */
  googleTwoFactor: GoogleTwoFactorState | null;
  /**
   * True until the next validation runs. Set when a draft comes back from
   * Google: the values in the form are then equal to the ones in state, and
   * the step's "don't validate the same input twice" shortcut would otherwise
   * wave the person straight past validation.
   */
  mustValidateCredentials: boolean;
  /** True while the redirect to Google, or the sign-in after it, is in flight. */
  isGoogleBusy: boolean;
  googleError: React.ReactNode;
  /** Google returned an address that belongs to another role. */
  isEmailInUseByAnotherRole: boolean;
};
export type MultiStepFormActions = {
  prevStep: () => void;
  nextStep: () => void;
  setCredentials: Dispatch<SetStateAction<CredentialsFormState>>;
  setStandDetails: Dispatch<SetStateAction<StandDetailsFormState>>;
  submitExhibitor: (data: SubmitExhibitorBody) => Promise<any>;
  resetValidationState: () => void;

  setSignUpResult: Dispatch<SetStateAction<SignUpResult>>;
  setCredentialsDraftReader: (
    read: (() => CredentialsFormState) | null
  ) => void;
  startGoogleSignUp: () => Promise<void>;
  completeGoogleTwoFactor: (customToken: string) => Promise<void>;
  cancelGoogleTwoFactor: () => void;
  /** Starts the session once the account behind the identity exists. */
  completeGoogleSignUp: () => Promise<void>;
};
export type SubmitExhibitorBody = CredentialsFormState & {
  showId?: string;
  token?: string;
};
